import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Toaster, toast } from "sonner";
import { CheckCircle2, AlertCircle, Lock, User } from "lucide-react";

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const initial = useRef(false);

  useEffect(() => {
    if (initial.current) return;
    initial.current = true;
    if (!token) {
      setError("Lien invalide");
      setLoading(false);
      return;
    }
    api.get(`/auth/invite-info/${token}`)
      .then(({ data }) => {
        setInfo(data);
        setName(data.name || "");
        setLoading(false);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Lien invalide ou expiré");
        setLoading(false);
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Mot de passe trop court (8 caractères minimum)"); return; }
    if (password !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password, name });
      localStorage.setItem("session_token", data.session_token);
      setUser(data.user);
      toast.success("Compte activé !");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Activation impossible");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="progress-line w-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="ra-card p-8 max-w-md text-center">
          <AlertCircle size={40} className="mx-auto mb-3 text-red-500" />
          <h1 className="font-heading font-black text-2xl mb-2">Lien invalide</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Demandez à votre administrateur de générer un nouveau lien d'invitation.</p>
        </div>
      </div>
    );
  }

  const roleLabel = { admin: "Administrateur", vp: "Vice-président", user: "Utilisateur" }[info?.role] || info?.role;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-neutral-900 dark:to-neutral-950">
      <Toaster richColors position="top-right" />
      <div className="ra-card p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#002FA7] text-white text-xs font-bold uppercase tracking-wide rounded-full mb-3">
            <CheckCircle2 size={12} /> Invitation
          </div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Activez votre compte</h1>
          <p className="text-sm text-gray-500 mt-2">Bienvenue ! Choisissez votre mot de passe pour finaliser votre inscription.</p>
        </div>

        <div className="bg-blue-50 dark:bg-blue-950/30 border-l-4 border-[#002FA7] p-3 mb-5 text-sm">
          <div className="text-gray-500 text-xs uppercase tracking-wide font-bold">Compte</div>
          <div className="font-medium">{info?.email}</div>
          <div className="text-xs text-gray-500 mt-0.5">Rôle : <strong>{roleLabel}</strong></div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="ra-label"><User size={12} className="inline mr-1" /> Votre nom</label>
            <input
              type="text"
              className="ra-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="invite-name"
            />
          </div>
          <div>
            <label className="ra-label"><Lock size={12} className="inline mr-1" /> Nouveau mot de passe</label>
            <input
              type="password"
              className="ra-input"
              placeholder="Au moins 8 caractères"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              data-testid="invite-password"
            />
          </div>
          <div>
            <label className="ra-label"><Lock size={12} className="inline mr-1" /> Confirmer le mot de passe</label>
            <input
              type="password"
              className="ra-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              data-testid="invite-confirm"
            />
            {confirm && password !== confirm && (
              <p className="text-xs text-red-500 mt-1">Les mots de passe ne correspondent pas</p>
            )}
          </div>
          <button
            type="submit"
            className="btn-primary w-full justify-center py-3"
            disabled={saving || !password || password !== confirm}
            data-testid="invite-submit"
          >
            {saving ? "Activation..." : "Activer mon compte →"}
          </button>
        </form>
      </div>
    </div>
  );
}
